import { useEffect, useState, useRef } from 'react';
import { useSearchParams } from 'react-router';
import { useAuth } from '../context/AuthContext';
import { exchangeToken, getCurrentUser } from '../api';
import { networkManager } from '../api/NetworkManager';
import { Loader2 } from 'lucide-react';

export function AuthCallback() {
  const [searchParams] = useSearchParams();
  const { setTokens, login } = useAuth();
  const [error, setError] = useState<string | null>(null);
  const exchangeStarted = useRef(false);

  useEffect(() => {
    // Prevent double execution in React StrictMode (dev)
    // Auth codes are single-use — a second call causes invalid_grant
    if (exchangeStarted.current) return;

    const code = searchParams.get('code');
    const errorParam = searchParams.get('error');

    if (errorParam) {
      setError(searchParams.get('error_description') || 'Authentication failed');
      return;
    }

    if (!code) {
      login();
      return;
    }

    exchangeStarted.current = true;

    const redirectUri =
      import.meta.env.VITE_COGNITO_REDIRECT_URI || `${window.location.origin}/auth/callback`;

    exchangeToken(code, redirectUri)
      .then(async (tokens) => {
        const idToken = tokens.id_token;
        setTokens(idToken, tokens.refresh_token);
        networkManager.setToken(idToken);

        await getCurrentUser();

        window.location.href = '/';
      })
      .catch((err) => {
        console.error('Token exchange failed:', err);
        setError('Failed to complete authentication. Please try again.');
        exchangeStarted.current = false;
      });
  }, [searchParams, setTokens, login]);

  if (error) {
    return (
      <div className="flex h-screen flex-col items-center justify-center gap-4">
        <p className="text-status-error">{error}</p>
        <button onClick={login} className="btn-primary">
          Try again
        </button>
      </div>
    );
  }

  return (
    <div className="flex h-screen flex-col items-center justify-center gap-4">
      <Loader2 className="h-8 w-8 animate-spin text-ink-tertiary" />
      <p className="text-ink-secondary">Signing in...</p>
    </div>
  );
}
